import { call, put } from 'redux-saga/effects';
import {
  updateUserValidator,
  validatePhoneNumber,
  FeatureFlagKey,
  DeviceType,
} from '@rapid-recovery-agency-inc/insightt-shared';
import { push } from 'connected-react-router';
import Cookies from 'js-cookie';

import { InsighttNotification } from '../../helpers/insightt-notification';
import storage from '../../helpers/storage';
import { clearPersistedAuthSession } from '../../helpers/clear-persisted-auth-session';
import AuthActions from '../actions/auth.actions';
import AppActions from '../actions/app.actions';
import ReportsActions from '../actions/reports.actions';
import GoalsActions from '../actions/goals.actions';
import TodoListActions from '../actions/todolist.actions';
import TaskManagementActions from '../actions/taskmanagement.actions';
import HumanResourcesActions from '../actions/humanresources.actions';
import ChecklistActions from '../actions/checklist.actions';
import ShiftsActions from '../actions/shifts.actions';
import ShiftDetailsActions from '../actions/shift-details.actions';
import ShiftActions from '../actions/shift.actions';
import StatsActions from '../actions/stats.actions';
import { getIsSuperAdmin } from '../../utils/users.utils';
import { getAuthPasswordErrorMessage } from '../../utils/password.utils';
import { getQueryStringOverview, getSearchParam } from '../../shared/html';
import { InMemoryCache } from '../../shared/cache';
import { setOpenAddBillingAddressModal } from '../../modules/companies/companies-slice';
import { trackLogin } from '../../modules/auth/events';
import { identify } from '../../shared/analytics/services';
import { store } from '../store';
// T067 — explicit logout runs through the one sign-out owner (full local cleanup first).

import { markSignInLanding, resolveReturnDestination, signOut } from '../../modules/auth';

// TODO Move to shared library when in monorepo

export function* clearRequest() {
  yield InMemoryCache.clear();
  yield put(AuthActions.clearAuthRequest());
  yield put(AppActions.clearAppRequest());
  yield put(ReportsActions.clearReportsRequest());
  yield put(GoalsActions.clearGoalsRequest());
  yield put(TodoListActions.clearTodoListRequest());
  yield put(TaskManagementActions.clearTasksRequest());
  yield put(HumanResourcesActions.clearHumanResourcesRequest());
  yield put(ChecklistActions.clearChecklistRequest());
  yield put(ShiftsActions.clearShiftsRequest());
  yield put(ShiftDetailsActions.clearShiftDetailsRequest());
  yield put(ShiftActions.clearShiftRequest());
  yield put(StatsActions.clearStatsRequest());
}

// eslint-disable-next-line complexity
export function* loginRequest(api, action) {
  const { payload, history } = action;

  let response;

  if (payload.crossLogin) {
    response = yield api.crossLogin({ token: payload.token });
  } else {
    response = yield api.loginByEmail(payload);
  }

  if (response && response.ok) {
    const { user, token, deviceToken, timezone, subscription } = response.data;

    if (typeof user.phoneNumber === 'string' && user.phoneNumber.startsWith('+1')) {
      user.phoneNumber = user.phoneNumber.slice(2);
    }

    const isSuperAdmin = getIsSuperAdmin(user);
    Cookies.set('access_token', token);
    Cookies.set('user_type', 'user-login');
    storage.set('user', JSON.stringify(user));
    storage.set('subscription', JSON.stringify(subscription));
    storage.set('timezone', timezone);

    const userId = `${user.company?.id ?? 'N/C'}:${user.email}`;
    window.Intercom('boot', {
      app_id: 'gaz7e1i2',
      email: user.email,
      user_id: userId,
      first_name: user.firstName,
      last_name: user.lastName,
      name: `${user.firstName} ${user.lastName}`,
      phone_number: user.phoneNumber,
      avatar_url: user.avatarUrl,
      role_id: user.roleId,
      role_name: user.role?.name,
      status: user.status,
      company_id: user.company?.id,
      company_name: user.company?.name,
      company_status: user.company?.status,
      branch_id: user.branch?.id,
      branch_name: user.branch?.name,
      is_alpha_tester: user.isAlphaTester,
      created_at: user.createdAt,
    });

    yield put(setOpenAddBillingAddressModal(!user.company.billingAddress));
    yield put(
      AuthActions.loginSuccess({
        user,
        token,
        deviceToken,
        timezone,
      }),
    );

    identify({
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role?.role,
      roleId: user.role?.id,
      company: user.company?.name,
      companyId: user.company.id,
      phoneNumber: user.phoneNumber,
      tier: subscription.totalSeats,
      deviceType: DeviceType.web,
      type: user.role?.type,
      subscription,
    });

    const redirectParam = getSearchParam('redirect');
    const redirectEnabled =
      store.getState().system.session.featureFlagsMap[FeatureFlagKey.WEB_ENABLE_REDIRECT_IF_NO_SESSION];

    if (isSuperAdmin && !user.company.isRegistrationCompleted) {
      history.push('/new-company');
    } else if (user.isPasswordChangeRequired) {
      history.push('/app/settings/manage-account#change-password');
      InsighttNotification(
        'warning',
        'Please change your password! Since you are using the default password.',
        null,
        ' ',
      );
    } else if (redirectParam && redirectEnabled) {
      // Same validation as the SSO path: only a same-origin path survives (FR-024, T086).
      const destination = resolveReturnDestination(redirectParam);
      markSignInLanding(destination);
      history.push(destination);
    } else {
      markSignInLanding('/app/overview');
      history.push({
        pathname: '/app/overview',
        search: getQueryStringOverview(),
      });
    }

    if (response.data.message) {
      InsighttNotification('success', response.data.message, response);
    }

    setTimeout(() => trackLogin(user.role?.name), 5000);
  } else {
    InsighttNotification('error', response.data ? response.data : response?.problem, response);
    yield put(AuthActions.loginFailure());
  }
}

export function* registerDeviceTokenRequest(api, action) {
  const { payload } = action;
  const response = yield api.registerDeviceToken(payload);
  if (response && response.ok) {
    storage.set('device_token', payload.deviceToken);
    yield put(AuthActions.registerDeviceTokenSuccess(payload.deviceToken));
  } else {
    InsighttNotification('error', 'Failure to register device token', response);
    yield put(AuthActions.registerDeviceTokenFailure());
  }
}

export function* logoutRequest(api, action) {
  const deviceToken = storage.get('device_token') || null;
  const payload = { deviceType: 'web', deviceToken };

  // T067 (FR-014, FR-021, FR-022; US5 AC1): the sign-out owner clears every piece of local state
  // first, then ends the legacy server session (with the token captured before clearing) or the SSO
  // provider session, then navigates exactly once — to `action.redirectUrl` or the sign-in page.
  yield call(signOut, {
    trigger: 'explicit',
    destination: action?.redirectUrl,
    terminateLegacyServerSession: async (accessToken) => {
      const response = await api.logout(payload, accessToken);
      // A 401 means the server session had already ended.
      return Boolean(response?.ok) || response?.status === 401;
    },
    releaseDeviceToken: deviceToken ? (accessToken) => api.unregisterDeviceToken(payload, accessToken) : undefined,
  });
  // The store reset comes last, as on main: by now the cleared session has redirected the protected
  // routes to the sign-in page, so `MainRouter` never renders against the emptied session user.
  yield put(AuthActions.logoutSuccess());
}

export function* forgotPasswordRequest(api, action) {
  const { payload } = action;
  const response = yield api.forgotPassword(payload);
  if (response && response.ok) {
    yield put(AuthActions.forgotPasswordSuccess());
    InsighttNotification('success', "We've sent you an email to update your password", response);
    yield put(push('/user/login'));
  } else {
    const errorMsg = response.data;
    InsighttNotification('error', errorMsg, response);
    yield put(AuthActions.forgotPasswordFailure());
  }
}

export function* resetPasswordRequest(api, action) {
  const { payload } = action;
  const response = yield api.resetPassword(payload);
  if (response && response.ok) {
    yield put(AuthActions.resetPasswordSuccess());
    InsighttNotification('success', "We've updated your password successfully", response);
    yield put(push('/user/login'));
  } else {
    const message = getAuthPasswordErrorMessage(response, 'Reset Password Failure');
    InsighttNotification('error', message);
    yield put(AuthActions.resetPasswordFailure(message));
  }
}

export function* updateUserInformationRequest(api, action) {
  try {
    const { payload, user } = action;
    delete payload?.email;
    const errorMessage = updateUserValidator(payload, payload?.skipAvatarValidation);

    if (payload?.avatar === user?.avatarUrl) delete payload.avatar;

    if (errorMessage) {
      InsighttNotification('error', errorMessage);
      yield put(AuthActions.updateUserInformationFailure());
      return;
    }

    // Add +1 to send the phone number in the correct format to update database information
    if (payload && payload.phoneNumber) {
      const plainPhoneNumber = payload.phoneNumber.replace(/[^\d]/g, '');

      payload.phoneNumber = `+1${plainPhoneNumber}`;
    }
    const response = yield api.updateMyProfile(payload);

    if (response && response.ok) {
      // Remove +1 before updating the current profile; the reducer owns legacy persistence.
      yield put(
        AuthActions.updateUserInformationSuccess({
          ...response.data,
          phoneNumber: validatePhoneNumber(response.data.phoneNumber),
        }),
      );
      InsighttNotification('success', 'Your information was updated successfully', response);
    } else {
      InsighttNotification('error', 'Unable to update your information', response);
      yield put(AuthActions.updateUserInformationFailure());
    }
  } catch {
    InsighttNotification('error', 'Unable to update your information');
    yield put(AuthActions.updateUserInformationFailure());
  }
}

export function* updatePasswordRequest(api, action) {
  const { payload } = action;
  const response = yield api.updatePassword(payload);
  if (response && response.ok) {
    yield put(AuthActions.updatePasswordSuccess(response.data));
    InsighttNotification('success', 'Your password was updated successfully', response);
  } else {
    yield put(AuthActions.updatePasswordFailure());
    InsighttNotification('error', 'Unable to update password', response);
  }
}

export function* acceptInviteRequest(api, action) {
  // Clear previous store if user has been logged in the same browsser
  yield clearRequest();
  clearPersistedAuthSession();

  const { payload } = action;
  const response = yield api.acceptInvite(payload);
  if (response && response.ok) {
    InsighttNotification('success', 'You have accepted the invitation successfully', response);
    yield put(AuthActions.acceptInviteSuccess(response.data));
  } else {
    InsighttNotification('error', 'An error occurred while accepting the invitation', null, 'Alert!', null, 5000);
    yield put(AuthActions.acceptInviteFailure(response.data));
  }
}

export function* setPasswordRequest(api, action) {
  const { payload } = action;
  const response = yield api.setPassword(payload);
  if (response && response.ok) {
    // Clear any prior signed-in session before leaving the invitation flow.
    // Do not logout/redirect on failed attempts so the user can retry on this page.
    clearPersistedAuthSession();
    InsighttNotification('success', 'You updated your password successfully', response);
    yield put(AuthActions.setPasswordSuccess(response.data));
    yield put(push('/user/login'));
  } else {
    const message = getAuthPasswordErrorMessage(response, 'Update Password Failure');
    InsighttNotification('error', message);
    yield put(AuthActions.setPasswordFailure(message));
  }
}

export function* getMyCompanyRequest(api) {
  const response = yield api.getMyCompany();
  if (response && response.ok) {
    yield put(AuthActions.getMyCompanySuccess(response.data));
  } else {
    InsighttNotification('error', 'Unable to get company', response);
    yield put(AuthActions.getMyCompanyFailure());
  }
}
